import type { HTTPService } from "@soft-db/http";

export const DashboardScopeType = {
	DefaultSpace: "space",
	User: "user",
	PublicPage: "public_page",
} as const;
export type DashboardScopeType = (typeof DashboardScopeType)[keyof typeof DashboardScopeType];

export type DashboardScope = {
	type: DashboardScopeType;
	id: string;
};

export type SeverityLimits = {
	exceedance: string;
	warning: string;
};

export type DashboardConfig = {
	vibrationCriterias: SeverityLimits;
};

export type Dashboard = {
	id: string;
	scope: DashboardScope | null;
	template?: { panels: DashboardPanel[] };
	panels: DashboardPanel[];
	config: DashboardConfig;
};

export type DashboardTemplateContent = {
	panels: DashboardPanel[];
};

export const isDashboardTemplate = (value: any): value is DashboardTemplateContent => {
	return !!value.panels && Array.isArray(value.panels);
};

export type DashboardPanel = {
	id: string;
	title: string;
	position: { x: number | null; y: number | null; width: number; height: number };
	plugin: PanelPlugin;
};

export type ConfigBlock = Record<string, unknown>;

export type DataConfigBlock = {
	dynamicImagee?: string | null;
	vibrationCriteria?: SeverityLimits | null;
} & ConfigBlock;

export type PanelPlugin = {
	version: number;

	type: string;
	options: ConfigBlock | null | undefined;
	data: DataConfigBlock | null | undefined;
};

export type DashboardSearchOptions = {
	scopeType?: DashboardScopeType;
	scopeId?: string;
};

export class DashboardApi {
	constructor(private _http: HTTPService) {}

	async fetch(siteId: string, options?: DashboardSearchOptions) {
		return await this._http.get<Dashboard[]>(`sites/${siteId}/dashboards`, {
			params: { ...options },
		});
	}

	async fetchDashboard(siteId: string, dashboardId: string) {
		return await this._http.get<Dashboard>(`sites/${siteId}/dashboards/${dashboardId}`);
	}

	async fetchAllDashboards(siteId: string) {
		return await this._http.get<Dashboard[]>(`sites/${siteId}/dashboards/all`);
	}

	async create(siteId: string, options: DashboardSearchOptions, cloneFrom?: string) {
		return await this._http.post<Dashboard>(`sites/${siteId}/dashboards`, options, {
			params: { cloneFrom },
		});
	}

	async update(
		siteId: string,
		dashboardId: string,
		config: DashboardConfig,
		panels: DashboardPanel[]
	) {
		return await this._http.patch<Dashboard>(`sites/${siteId}/dashboards/${dashboardId}`, {
			vibrationCriterias: config.vibrationCriterias,
			panels: panels,
		});
	}

	async delete(siteId: string, dashboardId: string) {
		return await this._http.delete(`sites/${siteId}/dashboards/${dashboardId}`);
	}
}
